import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/formatCurrency';
import { useTranslation } from 'react-i18next';

const Receipt = ({ device, companyData, showQR = false, language = null }) => {
  const { t: baseT, i18n } = useTranslation();
  
  // Se viene specificata una lingua, usa quella, altrimenti usa la lingua di sistema
  const t = (key, options) => {
    if (language) {
      return i18n.t(key, { ...options, lng: language });
    }
    return baseT(key, options);
  };

  const calculateTotal = () => {
    if (device.paymentStatus === 'pagato') {
      return 0;
    }
    const price = parseFloat(device.price) || 0;
    const deposit = parseFloat(device.deposit) || 0;
    return price - deposit;
  };

  // Gestione sicura dei problemi con traduzione
  const formatProblems = (problems) => {
    if (!problems) return '';
    if (typeof problems === 'string') {
      // Gestione del caso in cui il problema sia "Altro:qualcosa"
      if (problems.startsWith('Altro:')) {
        const customProblem = problems.substring(6); // Rimuove "Altro:" dal problema
        return `${t('models.device.problems.Altro')}: ${customProblem}`;
      }
      return t(`models.device.problems.${problems}`);
    }
    if (Array.isArray(problems)) {
      return problems
        .filter(p => p && p.trim()) // Filtra valori vuoti
        .map(p => {
          // Gestione del caso in cui il problema sia "Altro:qualcosa"
          if (p.startsWith('Altro:')) {
            const customProblem = p.substring(6); // Rimuove "Altro:" dal problema
            return `${t('models.device.problems.Altro')}: ${customProblem}`;
          }
          return t(`models.device.problems.${p}`);
        })
        .join(', ');
    }
    return '';
  };

  // Verifica che i dati necessari siano presenti
  if (!device || !companyData) {
    console.error('Dati mancanti per la ricevuta:', { device, companyData });
    return null;
  }

  return (
    <div className="receipt">
      <div className="receipt-header">
        <h3>{companyData.companyName || t('companyName')}</h3>
        <p>{companyData.address || t('address')}</p>
        {companyData.region && <p>{companyData.region}</p>}
        <p>Tel: {companyData.phone || t('phone')}</p>
      </div>

      <div className="receipt-content">
        <h4>{t('receipt.title')} {device.repairNumber || ''}</h4>
        <p>{t('receipt.date')}: {device.entryDate ? formatDate(device.entryDate) : formatDate(new Date())}</p>
        
        <div className="device-info">
          <p>{t('receipt.device.info')}: {device.brand || ''} {device.model || ''}</p>
          <p>{t('receipt.device.problem')}: {formatProblems(device.problems)}</p>
          {device.serialNumber && <p>{t('receipt.device.serialNumber')}: {device.serialNumber}</p>}
          {device.customerPhone && <p>{t('receipt.device.customerPhone')}: {device.customerPhone}</p>}
        </div>

        <div className="price-info">
          <p>{t('receipt.payment.repairPrice')}: {formatCurrency(parseFloat(device.price || 0), companyData.currency)}</p>
          {device.deposit > 0 && (
            <p>{t('receipt.payment.deposit')}: {formatCurrency(parseFloat(device.deposit), companyData.currency)}</p>
          )}
          <p><strong>
            {device.paymentStatus === 'pagato' ? 
              t('receipt.payment.paid') : 
              `${t('receipt.payment.toPay')}: ${formatCurrency(calculateTotal(), companyData.currency)}`
            }
          </strong></p>
        </div>

        {device.notes && (
          <div className="notes">
            <p>{t('receipt.notes')}: {device.notes}</p>
          </div>
        )}

        {device.deliveryDate && (
          <p>{t('receipt.expectedDelivery')}: {formatDate(device.deliveryDate)}</p>
        )}
      </div>

      {showQR && companyData.websiteUrl && (
        <div className="receipt-qr">
          <QRCodeSVG
            value={`${companyData.websiteUrl}/repair/${device.repairNumber}`}
            size={100}
            level="L"
          />
        </div>
      )}

      <div className="receipt-footer">
        <p>{companyData.disclaimer || t('receipt.footer.thanks')}</p>
        <p>{t('receipt.footer.warranty.prefix')} {companyData.warrantyDays || 30} {t('receipt.footer.warranty.suffix')}</p>
      </div>
    </div>
  );
};

export default Receipt; 